import * as yup from 'yup';
import {
  InterestRateFrequency,
  InterestTypes,
  PreFixedTypes,
  SimulationTypes,
  YesNoOptions,
} from './types';
import dayjs from 'dayjs';
import { differenceInDays } from 'date-fns';
import { ConsigneeOrganType } from '../../ConsignedStep/interfaces';

export const getValueForSelect = <T>(
  allValues: Array<{ value: T; label: string }>,
  value?: T | null,
): { value: T; label: string } => {
  const valueContent = allValues.find((item) => item.value === value);
  return valueContent ?? allValues[0];
};

export const getValueForSelectMulti = <T>(
  allValues: Array<{ value: T; label: string }>,
  value?: T[] | null,
): Array<{ value: T; label: string }> => {
  const valueContent = allValues.filter((item) => value?.includes(item.value));
  return valueContent;
};

export const getFormValidationSchema = (
  isMultiplesDisbursementDates?: boolean,
  organType?: string,
) =>
  yup.object({
    amount: yup
      .string()
      .required('Campo obrigatório')
      .test({
        name: 'amount field',
        test: (value: unknown, { createError }) => {
          return value === '0,00'
            ? createError({
                message: 'Insira um valor válido para simular',
                path: 'amount',
              })
            : true;
        },
      }),
    numPayments: yup.string().required('Campo obrigatório'),
    tacAmount: yup.string().required('Campo obrigatório'),
    additionalInstallmentFee: yup
      .string()
      .nullable()
      .test({
        name: 'additionalInstallmentFee field',
        test: (value: unknown, { createError, parent }) => {
          if (parent.hasAdditionalFee !== YesNoOptions.YES) return true;
          return value === '0'
            ? createError({
                message: 'Insira um valor maior que 0',
              })
            : !value
            ? createError({
                message: 'Campo obrigatório',
              })
            : true;
        },
      }),
    additionalInstallmentDescription: yup
      .string()
      .nullable()
      .test({
        name: 'additionalInstallmentDescription field',
        test: (value: unknown, { createError, parent }) => {
          if (parent.hasAdditionalFee !== YesNoOptions.YES) return true;
          return !value || (value as string).length === 0
            ? createError({
                message: 'Campo obrigatório',
              })
            : true;
        },
      }),
    interestPosRate: yup
      .string()
      .nullable()
      .test({
        name: 'interestPosRate field',
        test: (value: unknown, { createError, parent }) => {
          if (parent.interestType !== InterestTypes.POS) return true;
          return value === '0'
            ? createError({
                message: 'Insira um valor maior que 0',
              })
            : !value
            ? createError({
                message: 'Campo obrigatório',
              })
            : true;
        },
      }),
    financeFee: yup.string().required('Campo obrigatório'),
    insurance_amount: yup
      .string()
      .required('Campo obrigatório')
      .test({
        name: 'insurance_amount field',
        test: (value: unknown, { createError, parent }) => {
          if (!value || value === '0,00') return true;

          const insuranceValue =
            Number((value as string).replace(/[^\d,]/g, '').replace(',', '.')) /
            100;
          const amountValue =
            Number(
              (parent.amount as string)
                .replace(/[^\d,]/g, '')
                .replace(',', '.'),
            ) / 100;

          return insuranceValue >= amountValue
            ? createError({
                message:
                  'Valor do seguro deve ser menor que o valor da operação.',
                path: 'insurance_amount',
              })
            : true;
        },
      }),
    iofType: yup.string().nullable().required('Campo obrigatório'),
    firstPaymentDate: yup
      .string()
      .nullable()
      .required('Campo obrigatório')
      .test({
        name: 'firstPaymentDate field',
        test: (value: unknown, { createError, parent }) => {
          return dayjs(value as string).isBefore(
            dayjs(parent.disbursementDate),
            'day',
          )
            ? createError({
                message: `Valor deve ser ${
                  !isMultiplesDisbursementDates ? 'igual ou ' : ''
                }posterior à data de liberação`,
                path: 'firstPaymentDate',
              })
            : !parent.disbursementDate
            ? createError({
                message: 'Preencha a data de liberação do recurso',
                path: 'firstPaymentDate',
              })
            : isMultiplesDisbursementDates && value === parent.disbursementDate
            ? createError({
                message:
                  'Valor deve ser superior à data de liberação do recurso',
                path: 'firstPaymentDate',
              })
            : true;
        },
      }),
    disbursementDate: yup
      .string()
      .nullable()
      .required('Campo obrigatório')
      .test({
        name: 'disbursementDate field',
        test: (value: unknown, { createError }) => {
          if (
            !isMultiplesDisbursementDates &&
            new Date(value as string) <
              new Date(new Date().setHours(0, 0, 0, 0))
          ) {
            return createError({
              message: 'Valor deve ser superior à data atual',
              path: 'disbursementDate',
            });
          }

          if (isMultiplesDisbursementDates) {
            const selectedDate = new Date(
              new Date(value as string).setHours(0, 0, 0, 0),
            );
            const today = new Date(new Date().setHours(0, 0, 0, 0));

            if (organType === ConsigneeOrganType.CT) {
              if (selectedDate < today) {
                return createError({
                  message: 'Valor deve ser superior ou igual à data atual',
                  path: 'disbursementDate',
                });
              }

              if (differenceInDays(selectedDate, today) > 14) {
                return createError({
                  message:
                    'A data de liberação do recurso não pode ser superior a 15 dias',
                  path: 'disbursementDate',
                });
              }
            } else {
              if (selectedDate <= today) {
                return createError({
                  message: 'Valor deve ser superior à data atual',
                  path: 'disbursementDate',
                });
              }

              // 14 dias a partir de hoje = 15 dias total
              if (differenceInDays(selectedDate, today) > 14) {
                return createError({
                  message:
                    'A data de liberação do recurso não pode ser superior a 15 dias',
                  path: 'disbursementDate',
                });
              }
            }
          }

          return true;
        },
      }),
  });

export const getValidDisbursementDate = (
  isMultiplesDisbursementDates?: boolean,
) => {
  if (isMultiplesDisbursementDates) {
    return new Date(new Date().setDate(new Date().getDate() + 1)).toISOString();
  }
  return new Date().toISOString();
};

export const getInterestTypeOptions = (type: SimulationTypes) => {
  const options = {
    [SimulationTypes.IRREGULAR]: [
      { value: InterestTypes.POS, label: 'Pós-fixado' },
    ],
    [SimulationTypes.PRICE]: [
      { value: InterestTypes.PRE, label: 'Pré-fixado' },
      { value: InterestTypes.POS, label: 'Pós-fixado' },
    ],
    [SimulationTypes.SAA]: [{ value: InterestTypes.PRE, label: 'Pré-fixado' }],
    [SimulationTypes.SAC]: [{ value: InterestTypes.PRE, label: 'Pré-fixado' }],
  };

  return options[type];
};

export const getInterestBaseRateOptions = (interestType: InterestTypes) => {
  if (interestType === InterestTypes.POS) {
    return [{ value: PreFixedTypes.BASE_360, label: '360' }];
  }

  return [
    { value: PreFixedTypes.BASE_360, label: '360' },
    { value: PreFixedTypes.BASE_365, label: '365' },
  ];
};

export const getInterestRateFrequencyOptions = () => {
  return [
    { value: InterestRateFrequency.DAILY, label: 'Diário' },
    { value: InterestRateFrequency.MONTHLY, label: 'Mensal' },
    { value: InterestRateFrequency.ANNUALLY, label: 'Anual' },
  ];
};

export const isPreInterestRateOption = (
  interestType: InterestTypes,
  simulationType: SimulationTypes,
): boolean => {
  const isPreOptionFilled = interestType === InterestTypes.PRE;

  if (isPreOptionFilled) return true;

  if ([SimulationTypes.SAA, SimulationTypes.SAC].includes(simulationType)) {
    return true;
  }

  if (simulationType === SimulationTypes.PRICE && isPreOptionFilled)
    return true;

  return false;
};
